/**
 * Chat Service
 * 
 * Handles chat functionality for the CRM system including:
 * - Managing chat message history with database session persistence
 * - Streaming communication with the chat API backend
 * - Processing server-sent events (SSE) for real-time responses
 * - Server-owned tool progress display for new-chat SSE events
 * - Error handling and type safety with enhanced debugging capabilities
 * - Message length management to ensure payload stays within 4K character limit
 * 
 * Implementation notes:
 * - Centralizes auth header fetch to redirect on invalid refresh token
 * - Sends user/assistant messages plus durable ask_user_input tool responses to the new-chat protocol
 * - Canonicalizes and durably updates omitted composer capability only while an ask_user_input call is still pending
 *
 * Key Features:
 * - Message persistence: Stores chat history in database sessions
 * - Streaming responses: Handles chunked AI responses via SSE
 * - Tool progress: Displays server-owned tool.call and tool.result events
 * - History management: Maintains recent message history for context with 4K character limit
 * - Type compliance: Sends only new-chat accepted conversation roles and ask_user_input tool responses
 * - Character limit enforcement: Trims message history to stay within 4K character payload limit
 * - **Enhanced Error Logging**: Comprehensive debugging information for SSE event parsing and message handling
 * - **SSE Event Message Integration**: Creates UI-only messages for real-time progress feedback
 *   * tool.call/tool.result events → UI-only messages for server-side tool execution progress
 *   * Messages marked with isUIOnly=true are excluded from server communication
 *   * Maintains chronological order with proper filtering for LLM context
 * 
 * Enhanced Error Logging:
 * - Detailed SSE event parsing logs with full event content and structure analysis
 * - JSON parsing error details with malformed data identification
 * - Stream processing error tracking with buffer state information
 * - Request/response debugging with payload and header information
 * - Message history validation and debugging for troubleshooting conversation flow issues
 * 
 * Recent Changes:
 * - **JULY 31, 2026: FRONTEND DRAFT HANDOFFS** - Dispatches validated note/task UI actions and exactly-once external draft attempts without model-history tool calls.
 * - **JULY 30, 2026: DURABLE STOP + EDIT TITLES** - Persists one exact stopped marker and refreshes session titles after committed edits.
 * - **JULY 30, 2026: CLIENT HISTORY BOUNDARY** - Removes server-owned tool calls/results while preserving canonical ask_user_input pairs.
 * - **JULY 29, 2026: RESTORED HITL ANSWERS** - Reconstructs UI-only answer bubbles from exact canonical durable pairs.
 * - **JULY 29, 2026: HITL EDIT REPLACEMENT** - Atomically replaces a pending branch and authoritatively reconciles ambiguous acknowledgements.
 * - **JULY 29, 2026: STRUCTURED HITL ANSWERS** - Skips composer-capability persistence for declared options and cancellation.
 * - **JULY 28, 2026: CANONICAL COMPOSER HITL** - Persists and replays the same allowOther capability presented by the CRM composer.
 * - **JULY 28, 2026: RESTORED HITL CONTENT** - Normalizes textless persisted assistant tool calls before API resume.
 * - **JULY 28, 2026: LLM-RUNTIME 0.7 HITL** - Uses canonical answered outcomes, durable cancellation markers, and legacy-pair quarantine.
 * - **JULY 28, 2026: STOP CHAT** - Adds request-scoped cancellation, stopped partial output, and runtime abort propagation.
 * - **MAY 22, 2026: CLIENT-OWNED ASK_USER_INPUT** - Handles ask_user_input from assistant tool_calls instead of server HITL events.
 * - **MAY 22, 2026: RETIRED LEGACY CHAT TOOLS** - Removed client-side tool execution and tool response continuation loops.
 * - **NOVEMBER 17, 2025: API-SIDE MESSAGE PERSISTENCE** - Moved message persistence to API /chat endpoint
 *   - API now handles all database message saving automatically during chat streaming
 *   - Removed redundant saveMessageToSession calls from frontend
 *   - API loads conversation history from database and saves all messages (user, assistant, tool)
 *   - Ensures complete conversation history is available across sessions with zero frontend overhead
 * - **NOVEMBER 17, 2025: SESSION SWITCHING FIX** - Fixed "Must declare scalar variable @title" error
 *   - Updated switchSession() to use new setActiveSession() method
 *   - Uses dedicated PATCH endpoint for setting active session without requiring title parameter
 *   - Separates concerns: updateSession for title changes, setActiveSession for switching
 * - **NOVEMBER 17, 2025: AUTO-SESSION CREATION** - Added automatic session creation for seamless UX
 *   - Users automatically get a chat session created if they don't have one
 *   - Session creation happens during initialization and before sending first message
 *   - Uses new ensureActiveSession() method that calls GetOrCreateActiveSession procedure
 *   - No user action required - chat just works
 * - **JUNE 14, 2025: ENHANCED ERROR LOGGING** - Added comprehensive debugging information
 *   - Detailed SSE event parsing logs for troubleshooting malformed event data
 *   - JSON parsing error analysis with raw data inspection
 *   - Stream buffer state tracking for identifying data corruption issues
 *   - Request/response debugging with full payload and error context
 *   - Function execution debugging with parameter validation and result tracking
 * - MESSAGE EDITING: Added editMessage() method for user message editing with automatic history truncation
 *   - Allows editing of user messages and removes all subsequent messages from chat history
 *   - Preserves conversation flow integrity for LLM resubmission
 * - FEBRUARY 6, 2026: Redirect to login when auth headers fail due to invalid refresh token
 */

import {
  ChatMessage,
  ChatServiceError,
  HumanInputAnswer,
  HumanInputAnsweredOutcome,
  HumanInputRequest,
  PendingHumanInput
} from '../models/ChatMessage';
import {
  canonicalizeComposerHumanInputToolCalls,
  normalizeHumanInputOutcome,
  parseCanonicalHumanInputAnswer,
  parseHumanInputRequest
} from '../utils/humanInputContract';
import { getAuthHeaders, redirectToLogin, RefreshFailedError } from './authService';
import { EVENTS } from '../constants/eventConstants';
import { chatSessionService, ChatMessageDTO } from './chatSessionService';
import { buildGmailUrl, buildGoogleCalendarUrl, buildGoogleTasksUrl } from '../utils/googleUtils';
import {
  formatGoogleTaskClipboardText,
  parseClientActionEvent,
  type ClientActionEvent,
} from '../utils/frontendActionContract';

// Event bus interface for external communication
interface EventBus {
  publish: (event: string, data: any) => void;
}

const MAX_HISTORY_MESSAGES = 20;
const MAX_PAYLOAD_CHARACTERS = 80000; // 4K character limit for chat messages
const STORAGE_KEY_SESSION_ID = 'active_chat_session_id';

type ChatRequestState =
  | 'running'
  | 'stop-requested'
  | 'stopped'
  | 'completing'
  | 'completed'
  | 'failed';

interface ActiveChatRequest {
  id: number;
  controller: AbortController;
  reader: ReadableStreamDefaultReader<Uint8Array> | null;
  state: ChatRequestState;
  assistantContent: string;
  terminalMessage: ChatMessage | null;
  stopFinalization: Promise<ChatMessage> | null;
  progressMessageIds: Set<number>;
}

export interface ChatRequestHandle extends Promise<ChatMessage> {
  readonly requestId: number;
  stop: () => boolean;
}

const getAuthHeadersOrRedirect = async (): Promise<HeadersInit> => {
  try {
    return await getAuthHeaders();
  } catch (error) {
    if (error instanceof RefreshFailedError && error.status === 401) {
      redirectToLogin();
    } else if (error instanceof Error && error.message.includes('Unauthorized')) {
      redirectToLogin();
    }
    throw error;
  }
};

class ChatService {
  private messages: ChatMessage[] = [];
  private eventBus: EventBus | null = null;
  private currentSessionId: number | null = null;
  private initialized = false;
  private nextMessageId: number = 0; // Track next sequential messageId for database operations
  private nextRequestId = 1;
  private activeRequest: ActiveChatRequest | null = null;
  private sessionTransitionInProgress = false;
  private pendingHumanInputSubmissions = new Set<string>();
  private pendingCanonicalToolCallUpdates = new Set<string>();
  private historyReconciliationRequired = false;

  constructor() {
    // Initialization is now async - call initialize() before using service
  }

  /**
   * Initialize chat service with database session support
   * Auto-creates session if user doesn't have one
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      // Get or create active session (auto-creates if none exists)
      const activeSession = await chatSessionService.ensureActiveSession();
      this.currentSessionId = activeSession.id;

      // Load messages from active session
      await this.loadSessionMessages(activeSession.id);

      // Store session ID in localStorage for quick access
      if (this.currentSessionId) {
        localStorage.setItem(STORAGE_KEY_SESSION_ID, this.currentSessionId.toString());
      }

      this.initialized = true;
    } catch (error) {
      console.error('ChatService: Initialization failed', error);
      this.messages = [];
      this.initialized = true;
    }
  }

  /**
   * Load messages from a session
   */
  private async loadSessionMessages(sessionId: number): Promise<void> {
    try {
      const messageDTOs = await chatSessionService.getMessages(sessionId);
      this.applyLoadedSessionMessages(messageDTOs);
    } catch (error) {
      console.error('Failed to load session messages:', error);
      this.messages = [];
      this.nextMessageId = 0;
    }
  }

  private applyLoadedSessionMessages(messageDTOs: ChatMessageDTO[]): void {
    this.pendingCanonicalToolCallUpdates.clear();
    this.messages = messageDTOs.map(dto => this.convertFromDTO(dto));
    const maxMessageId = this.messages.reduce((max, msg) =>
      msg.messageId !== undefined && msg.messageId > max ? msg.messageId : max, -1);
    this.nextMessageId = maxMessageId + 1;
    this.historyReconciliationRequired = false;
    this.restoreCompletedHumanInputAnswerMessages();
    this.restorePendingHumanInputPrompts();
  }

  private async reconcileSessionMessages(sessionId: number): Promise<void> {
    try {
      const messageDTOs = await chatSessionService.getMessagesAuthoritative(sessionId);
      this.applyLoadedSessionMessages(messageDTOs);
    } catch (error) {
      this.historyReconciliationRequired = true;
      throw new ChatServiceError(
        `Failed to reconcile chat history: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }



  /**
   * Convert ChatMessageDTO to ChatMessage (used when loading from database)
   */
  private convertFromDTO(dto: ChatMessageDTO): ChatMessage {
    // SQL BIGINT values may be serialized as strings. Keep IDs numeric at the
    // service boundary so edit arithmetic cannot concatenate (for example "4" + 1).
    const messageId = Number(dto.messageId);
    const id = Number(dto.id ?? dto.messageId);
    const eventData = dto.eventData ? JSON.parse(dto.eventData) : undefined;
    const isUIOnly = dto.isUIOnly ?? dto.isUiOnly ?? false;
    const isStopped =
      dto.role === 'assistant' &&
      isUIOnly === true &&
      eventData?.eventType === 'response.stopped' &&
      eventData?.status === 'stopped';

    return {
      id, // Use unique database Id as primary key, fallback to messageId for new messages
      messageId,
      role: dto.role as any,
      // Persisted assistant tool-call rows may have SQL NULL content. The chat
      // protocol still requires a string, so preserve them as an empty message.
      content: typeof dto.content === 'string' ? dto.content : '',
      timestamp: dto.timestamp,
      name: dto.name,
      tool_call_id: dto.toolCallId,
      tool_calls: dto.toolCalls ? JSON.parse(dto.toolCalls) : undefined,
      execution_source: dto.executionSource as any,
      function: dto.functionData ? JSON.parse(dto.functionData) : undefined,
      // The generic response camel-caser normalizes SQL's IsUIOnly to isUiOnly.
      // Accept both spellings so durable UI-only markers survive a database reload.
      isUIOnly,
      ...(isStopped ? { isStopped: true } : {}),
      sseEventType: dto.sseEventType as any,
      eventData,
    };
  }

  /**
   * Note: Message persistence to database is now handled by the API /chat endpoint.
   * The API automatically loads messages from DB and saves all new messages.
   */

  /**
   * Switch to a different session
   */
  async switchSession(sessionId: number): Promise<void> {
    if (this.activeRequest) {
      throw new ChatServiceError('Cannot switch chat sessions while a request is in progress');
    }
    if (this.sessionTransitionInProgress) {
      throw new ChatServiceError('Another chat session switch is already in progress');
    }

    this.sessionTransitionInProgress = true;
    try {
      // Set as active session using dedicated endpoint
      await chatSessionService.setActiveSession(sessionId);

      // Load new session messages
      this.currentSessionId = sessionId;
      await this.loadSessionMessages(sessionId);

      // Notify UI (add SESSION_CHANGED event if needed, or remove this line)
      // this.eventBus?.publish(EVENTS.SESSION_CHANGED, { sessionId });
    } catch (error) {
      console.error('Failed to switch session:', error);
      throw error;
    } finally {
      this.sessionTransitionInProgress = false;
    }
  }

  /**
   * Get current session ID
   */
  getCurrentSessionId(): number | null {
    return this.currentSessionId;
  }

  private isHumanInputCancellationMessage(
    message: ChatMessage,
    toolCallId?: string
  ): boolean {
    const pendingHumanInput = message.eventData?.details?.pendingHumanInput as PendingHumanInput | undefined;
    const outcome = message.eventData?.details?.outcome as HumanInputAnswer | undefined;
    return (
      message.isUIOnly === true &&
      message.role === 'sse_event' &&
      message.sseEventType === 'human_input' &&
      message.eventData?.eventType === 'ask_user_input' &&
      message.eventData?.status === 'cancelled' &&
      typeof pendingHumanInput?.toolCallId === 'string' &&
      (!toolCallId || pendingHumanInput.toolCallId === toolCallId) &&
      outcome?.status === 'cancelled' &&
      outcome.reason === 'skipped'
    );
  }

  private getCancelledHumanInputToolCallIds(messages: ChatMessage[]): Set<string> {
    return new Set(
      messages
        .filter((message) => this.isHumanInputCancellationMessage(message))
        .map((message) =>
          (message.eventData?.details?.pendingHumanInput as PendingHumanInput).toolCallId
        )
    );
  }

  private getLegacyHumanInputToolCallIds(messages: ChatMessage[]): Set<string> {
    const requests = new Map<string, HumanInputRequest>();
    const legacyToolCallIds = new Set<string>();

    for (const message of messages) {
      if (message.role !== 'assistant') {
        continue;
      }
      for (const toolCall of message.tool_calls ?? []) {
        if (toolCall.function.name !== 'ask_user_input') {
          continue;
        }
        const request = parseHumanInputRequest(toolCall.function.arguments);
        if (request) {
          requests.set(toolCall.id, request);
        }
      }
    }

    for (const message of messages) {
      if (
        message.role !== 'tool' ||
        message.name !== 'ask_user_input' ||
        !message.tool_call_id
      ) {
        continue;
      }
      const request = requests.get(message.tool_call_id);
      if (!request || !parseCanonicalHumanInputAnswer(request, message.content)) {
        legacyToolCallIds.add(message.tool_call_id);
      }
    }

    return legacyToolCallIds;
  }

  private buildRecentHistory(messages: ChatMessage[]) {
    const excludedHumanInputToolCallIds = new Set([
      ...this.getCancelledHumanInputToolCallIds(messages),
      ...this.getLegacyHumanInputToolCallIds(messages)
    ]);
    const validatedMessages = messages
      .filter((msg) => !msg.isUIOnly)
      .filter((msg) => msg.role === 'user' || msg.role === 'assistant' || msg.role === 'tool')
      .filter((msg) =>
        msg.role !== 'tool' ||
        (
          msg.name === 'ask_user_input' &&
          Boolean(msg.tool_call_id) &&
          !excludedHumanInputToolCallIds.has(msg.tool_call_id as string)
        )
      )
      .map((msg) => {
        if (msg.role !== 'assistant' || !msg.tool_calls?.length) {
          return msg;
        }

        const toolCalls = canonicalizeComposerHumanInputToolCalls(
          msg.tool_calls.filter((toolCall) =>
            toolCall.function.name === 'ask_user_input' &&
            !excludedHumanInputToolCallIds.has(toolCall.id)
          )
        ) ?? [];
        return {
          ...msg,
          tool_calls: toolCalls.length > 0 ? toolCalls : undefined
        };
      })
      .filter((msg) => {
        if (msg.role === 'assistant' && msg.tool_calls?.length) {
          return true;
        }

        return typeof msg.content === 'string' && msg.content.trim().length > 0;
      })
      .map((msg) => ({
        role: msg.role as 'user' | 'assistant' | 'tool',
        // Assistant tool calls are valid without display text, but the CRM API
        // transport requires content to remain a string.
        content: typeof msg.content === 'string' ? msg.content : '',
        ...(msg.name ? { name: msg.name } : {}),
        ...(msg.tool_call_id ? { tool_call_id: msg.tool_call_id } : {}),
        ...(msg.tool_calls ? { tool_calls: msg.tool_calls } : {})
      }));

    // Apply character limit constraint
    const trimmedMessages = this.trimToCharacterLimit(validatedMessages);
    const answeredHumanInputToolCallIds = new Set(
      messages
        .filter((message) =>
          !message.isUIOnly &&
          message.role === 'tool' &&
          message.name === 'ask_user_input' &&
          typeof message.tool_call_id === 'string'
        )
        .map((message) => message.tool_call_id as string)
    );
    const retainedAssistantToolCallIds = new Set(
      trimmedMessages.flatMap((message) =>
        message.role === 'assistant'
          ? (message.tool_calls ?? []).map((toolCall: NonNullable<ChatMessage['tool_calls']>[number]) => toolCall.id)
          : []
      )
    );
    const retainedToolResultIds = new Set(
      trimmedMessages
        .filter((message) => message.role === 'tool' && typeof message.tool_call_id === 'string')
        .map((message) => message.tool_call_id as string)
    );

    return trimmedMessages
      .filter((message) =>
        message.role !== 'tool' ||
        !message.tool_call_id ||
        !answeredHumanInputToolCallIds.has(message.tool_call_id) ||
        retainedAssistantToolCallIds.has(message.tool_call_id)
      )
      .map((message) => {
        if (message.role !== 'assistant' || !message.tool_calls?.length) {
          return message;
        }

        const toolCalls = message.tool_calls.filter((toolCall: NonNullable<ChatMessage['tool_calls']>[number]) =>
          !answeredHumanInputToolCallIds.has(toolCall.id) ||
          retainedToolResultIds.has(toolCall.id)
        );
        return {
          ...message,
          ...(toolCalls.length > 0 ? { tool_calls: toolCalls } : { tool_calls: undefined })
        };
      })
      .filter((message) =>
        message.role !== 'assistant' ||
        Boolean(message.tool_calls?.length) ||
        message.content.trim().length > 0
      );
  }

  getRecentHistory() {
    return this.buildRecentHistory(this.messages);
  }

  private toMessageDTO(message: ChatMessage): Omit<ChatMessageDTO, 'id' | 'chatSessionId' | 'createdAt' | 'createdBy'> | null {
    if (message.messageId === undefined) {
      return null;
    }

    return {
      messageId: message.messageId,
      role: message.role,
      content: message.content,
      timestamp: message.timestamp,
      name: message.name,
      toolCallId: message.tool_call_id,
      toolCalls: message.tool_calls ? JSON.stringify(message.tool_calls) : undefined,
      executionSource: message.execution_source,
      functionData: message.function ? JSON.stringify(message.function) : undefined,
      isUIOnly: message.isUIOnly ?? false,
      sseEventType: message.sseEventType,
      eventData: message.eventData ? JSON.stringify(message.eventData) : undefined,
    };
  }

  private async persistMessage(message: ChatMessage, required = false): Promise<void> {
    if (!this.currentSessionId || message.isUIOnly) {
      if (required) {
        throw new ChatServiceError('Unable to persist human input without an active chat session');
      }
      return;
    }

    const dto = this.toMessageDTO(message);
    if (!dto) {
      if (required) {
        throw new ChatServiceError('Unable to persist human input without a message identifier');
      }
      return;
    }

    try {
      await chatSessionService.insertMessage(this.currentSessionId, dto);
      this.eventBus?.publish(EVENTS.CHAT_SESSION_UPDATED, { sessionId: this.currentSessionId });
    } catch (error) {
      console.error('ChatService: Failed to persist chat message', error);
      if (required) {
        throw new ChatServiceError('Failed to persist human input response');
      }
    }
  }

  private async persistHumanInputCancellation(message: ChatMessage): Promise<void> {
    if (!this.currentSessionId || !this.isHumanInputCancellationMessage(message)) {
      throw new ChatServiceError('Unable to persist human input cancellation');
    }

    const dto = this.toMessageDTO(message);
    if (!dto) {
      throw new ChatServiceError('Unable to persist human input cancellation without a message identifier');
    }

    try {
      await chatSessionService.insertMessage(this.currentSessionId, dto);
      this.eventBus?.publish(EVENTS.CHAT_SESSION_UPDATED, { sessionId: this.currentSessionId });
    } catch (error) {
      console.error('ChatService: Failed to persist human input cancellation', error);
      throw new ChatServiceError('Failed to persist human input cancellation');
    }
  }

  private isStoppedAssistantMarker(message: ChatMessage): boolean {
    return (
      message.role === 'assistant' &&
      message.isUIOnly === true &&
      message.eventData?.eventType === 'response.stopped' &&
      message.eventData.status === 'stopped'
    );
  }

  private async persistStoppedAssistant(message: ChatMessage): Promise<void> {
    if (!this.currentSessionId || !this.isStoppedAssistantMarker(message)) {
      throw new ChatServiceError('Unable to persist stopped response');
    }

    const dto = this.toMessageDTO(message);
    if (!dto) {
      throw new ChatServiceError('Unable to persist stopped response without a message identifier');
    }

    try {
      await chatSessionService.insertMessage(this.currentSessionId, dto);
      this.eventBus?.publish(EVENTS.CHAT_SESSION_UPDATED, { sessionId: this.currentSessionId });
    } catch (error) {
      console.error('ChatService: Failed to persist stopped response', error);
      throw new ChatServiceError('Failed to persist stopped response');
    }
  }

  private async persistCanonicalHumanInputRequest(pendingHumanInput: PendingHumanInput): Promise<void> {
    const assistantMessage = this.messages.find((message) =>
      message.role === 'assistant' &&
      message.tool_calls?.some((toolCall) =>
        toolCall.id === pendingHumanInput.toolCallId &&
        toolCall.function.name === pendingHumanInput.toolName
      )
    );

    if (
      !this.currentSessionId ||
      !assistantMessage ||
      !assistantMessage.tool_calls?.length
    ) {
      throw new ChatServiceError('Unable to persist the canonical human input request');
    }

    try {
      await chatSessionService.canonicalizeMessageToolCall(
        this.currentSessionId,
        assistantMessage.id,
        pendingHumanInput.toolCallId
      );
      this.pendingCanonicalToolCallUpdates.delete(pendingHumanInput.toolCallId);
    } catch (error) {
      console.error('ChatService: Failed to persist canonical human input request', error);
      throw new ChatServiceError('Failed to persist the canonical human input request');
    }
  }

  private trimToCharacterLimit(messages: any[]): any[] {
    // Start with the most recent messages and work backwards
    const recentMessages = messages.slice(-MAX_HISTORY_MESSAGES);
    const result: any[] = [];
    let totalCharacters = 0;

    // Calculate total character count for messages
    const calculateMessageLength = (msg: any): number => {
      return JSON.stringify(msg).length;
    };

    // Add messages from most recent until we hit the character limit
    for (let i = recentMessages.length - 1; i >= 0; i--) {
      const message = recentMessages[i];
      const messageLength = calculateMessageLength(message);

      // If this is the first message and it's too large, truncate it instead of excluding it
      if (result.length === 0 && messageLength > MAX_PAYLOAD_CHARACTERS) {
        // Truncate the message content to fit within the limit
        const maxContentLength = MAX_PAYLOAD_CHARACTERS - 200; // Reserve space for JSON structure
        const truncatedMessage: any = {
          ...message,
          content: typeof message.content === 'string'
            ? message.content.substring(0, maxContentLength) + '... [message truncated due to length]'
            : message.content
        };
        result.unshift(truncatedMessage);
        totalCharacters = calculateMessageLength(truncatedMessage);
        break; // Can't fit any more messages
      } else if (totalCharacters + messageLength <= MAX_PAYLOAD_CHARACTERS) {
        result.unshift(message);
        totalCharacters += messageLength;
      } else {
        // If this message would exceed the limit, stop adding more
        break;
      }
    }

    return result;
  }

  private createRequestHandle(
    operation: (request: ActiveChatRequest) => Promise<ChatMessage>
  ): ChatRequestHandle {
    if (this.sessionTransitionInProgress) {
      return this.createRejectedRequestHandle(
        new ChatServiceError('Cannot start a chat request while switching sessions')
      );
    }
    if (this.activeRequest) {
      return this.createRejectedRequestHandle(
        new ChatServiceError('Another chat request is already in progress')
      );
    }

    const request: ActiveChatRequest = {
      id: this.nextRequestId++,
      controller: new AbortController(),
      reader: null,
      state: 'running',
      assistantContent: '',
      terminalMessage: null,
      stopFinalization: null,
      progressMessageIds: new Set<number>()
    };
    this.activeRequest = request;

    const stop = () => {
      if (request.state !== 'running') {
        return false;
      }

      request.state = 'stop-requested';
      request.controller.abort();
      if (request.reader) {
        void request.reader.cancel().catch(() => undefined);
      }
      return true;
    };

    let operationPromise: Promise<ChatMessage>;
    try {
      operationPromise = operation(request);
    } catch (error) {
      request.state = 'failed';
      operationPromise = Promise.reject(error);
    }

    const result = operationPromise
      .catch((error) => {
        this.markRequestFailed(request);
        throw error;
      })
      .finally(() => {
        if (this.activeRequest === request) {
          this.activeRequest = null;
        }
        request.reader = null;
      }) as ChatRequestHandle;

    Object.defineProperties(result, {
      requestId: {
        value: request.id,
        enumerable: true
      },
      stop: {
        value: stop,
        enumerable: true
      }
    });

    return result;
  }

  private createRejectedRequestHandle(error: unknown): ChatRequestHandle {
    const result = Promise.reject(error) as ChatRequestHandle;
    Object.defineProperties(result, {
      requestId: {
        value: this.nextRequestId++,
        enumerable: true
      },
      stop: {
        value: () => false,
        enumerable: true
      }
    });
    return result;
  }

  private markRequestFailed(request: ActiveChatRequest): void {
    if (
      request.state === 'running' ||
      request.state === 'stop-requested' ||
      request.state === 'completing'
    ) {
      request.state = 'failed';
    }
  }

  private isStopRequested(request: ActiveChatRequest): boolean {
    return request.state === 'stop-requested';
  }

  private isRequestedAbort(request: ActiveChatRequest, error: unknown): boolean {
    return (
      request.state === 'stop-requested' &&
      request.controller.signal.aborted &&
      error instanceof Error &&
      error.name === 'AbortError'
    );
  }

  private async abortRequestTransport(request: ActiveChatRequest): Promise<void> {
    request.controller.abort();
    if (request.reader) {
      await request.reader.cancel().catch(() => undefined);
    }
  }

  private markOwnedProgressStopped(request: ActiveChatRequest): void {
    for (const progressMessageId of request.progressMessageIds) {
      const message = this.messages.find((candidate) => candidate.id === progressMessageId);
      if (!message?.eventData || message.eventData.status !== 'pending') {
        continue;
      }

      message.content = 'Stopped';
      message.timestamp = new Date().toISOString();
      message.eventData = {
        ...message.eventData,
        status: 'stopped',
        message: 'Stopped'
      };
    }
  }

  private finalizeStoppedRequest(request: ActiveChatRequest): Promise<ChatMessage> {
    if (request.stopFinalization) {
      return request.stopFinalization;
    }

    if (request.state !== 'stop-requested') {
      if (request.terminalMessage) {
        return Promise.resolve(request.terminalMessage);
      }
      return Promise.reject(new ChatServiceError('Chat request could not be stopped'));
    }

    const stoppedMessage: ChatMessage = {
      id: Date.now() + Math.random(),
      messageId: this.nextMessageId++,
      role: 'assistant',
      content: request.assistantContent.trim()
        ? request.assistantContent
        : 'Response stopped.',
      timestamp: new Date().toISOString(),
      isUIOnly: true,
      isStopped: true,
      eventData: {
        eventType: 'response.stopped',
        status: 'stopped',
        message: 'Stopped'
      }
    };

    request.stopFinalization = (async () => {
      try {
        await this.persistStoppedAssistant(stoppedMessage);
      } catch (error) {
        request.state = 'failed';
        throw error;
      }

      request.state = 'stopped';
      this.markOwnedProgressStopped(request);
      request.terminalMessage = stoppedMessage;
      this.messages.push(stoppedMessage);
      this.saveMessages(this.messages);
      return stoppedMessage;
    })();

    return request.stopFinalization;
  }

  private async fetchChatResponse(
    request: ActiveChatRequest,
    onChunk: (content: string) => void
  ): Promise<ChatMessage> {
    if (this.isStopRequested(request)) {
      return this.finalizeStoppedRequest(request);
    }

    const authHeaders = await getAuthHeadersOrRedirect();
    if (this.isStopRequested(request)) {
      return this.finalizeStoppedRequest(request);
    }

    const response = await fetch('/api/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders
      },
      body: JSON.stringify({
        messages: this.getRecentHistory(),
        stream: true
      }),
      signal: request.controller.signal
    });

    if (!response.ok) {
      this.markRequestFailed(request);
      const errorText = await response.text();
      throw new ChatServiceError(errorText ? `HTTP ${response.status}: ${errorText}` : `HTTP error! status: ${response.status}`);
    }

    if (this.isStopRequested(request)) {
      return this.finalizeStoppedRequest(request);
    }

    return this.handleStreamingResponse(response, onChunk, request);
  }

  sendMessage(
    content: string,
    onChunk: (content: string) => void
  ): ChatRequestHandle {
    try {
      this.assertNoPendingHumanInput('send a new chat message');
    } catch (error) {
      return this.createRejectedRequestHandle(error);
    }

    return this.createRequestHandle(async (request) => {
      // Ensure user has an active session before sending message
      if (!this.currentSessionId) {
        try {
          const activeSession = await chatSessionService.ensureActiveSession();
          this.currentSessionId = activeSession.id;
        } catch (error) {
          console.error('ChatService: Failed to ensure session exists:', error);
          // Continue without session
        }
      }

      // Trim content if it exceeds character limit
      const trimmedContent = content.length > MAX_PAYLOAD_CHARACTERS
        ? content.substring(0, MAX_PAYLOAD_CHARACTERS - 100) + '... [message truncated]'
        : content;

      // Create user message with sequential messageId for database operations
      const userMessage: ChatMessage = {
        id: Date.now(),
        messageId: this.nextMessageId++,
        role: 'user',
        content: trimmedContent,
        timestamp: new Date().toISOString()
      };

      // Add user message to history
      this.messages.push(userMessage);
      this.saveMessages(this.messages);
      await this.persistMessage(userMessage);

      try {
        return await this.fetchChatResponse(request, onChunk);
      } catch (error) {
        if (this.isRequestedAbort(request, error)) {
          return this.finalizeStoppedRequest(request);
        }
        this.markRequestFailed(request);
        console.error('❌ Error in sendMessage:', {
          error: error instanceof Error ? error.message : 'Unknown error',
          errorStack: error instanceof Error ? error.stack : undefined,
          errorType: error?.constructor?.name,
          messageHistory: this.getRecentHistory(),
          userMessage: userMessage
        });
        throw new ChatServiceError(error instanceof Error ? error.message : 'Failed to send message');
      }
    });
  }

  private async handleStreamingResponse(
    response: Response,
    onChunk: (content: string) => void,
    request: ActiveChatRequest
  ): Promise<ChatMessage> {
    let reader: ReadableStreamDefaultReader<Uint8Array>;
    try {
      const responseReader = response.body?.getReader();
      if (!responseReader) {
        throw new ChatServiceError('No response body reader available');
      }
      reader = responseReader;
    } catch (error) {
      this.markRequestFailed(request);
      await this.abortRequestTransport(request);
      throw error instanceof ChatServiceError
        ? error
        : new ChatServiceError(error instanceof Error ? error.message : 'Unable to read response stream');
    }

    request.reader = reader;
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        if (request.state === 'stop-requested') {
          return this.finalizeStoppedRequest(request);
        }

        buffer += decoder.decode(value, { stream: true });

        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const evt of events) {
          if (evt.trim() === '') continue;

          try {
            const result = await this.processEvent(evt, onChunk, request);
            if (result) {
              return result;
            }
          } catch (error) {
            console.error('❌ Error processing SSE event:', {
              error: error instanceof Error ? error.message : 'Unknown error',
              errorStack: error instanceof Error ? error.stack : undefined,
              event: evt,
              bufferState: buffer,
              eventLength: evt.length,
              isEventEmpty: evt.trim() === ''
            });
            throw error;
          }
        }
      }

      if (request.state === 'stop-requested') {
        return this.finalizeStoppedRequest(request);
      }

      if (request.state === 'completed' && request.terminalMessage) {
        return request.terminalMessage;
      }

      if (request.state === 'running' && request.assistantContent) {
        const result = await this.completeAssistantRequest(request, request.assistantContent);
        if (result) {
          return result;
        }
      }

      this.markRequestFailed(request);
      throw new ChatServiceError('Stream ended without response');
    } catch (error) {
      if (this.isRequestedAbort(request, error)) {
        return this.finalizeStoppedRequest(request);
      }
      if (request.state === 'completed' && request.terminalMessage) {
        await this.abortRequestTransport(request);
        return request.terminalMessage;
      }
      this.markRequestFailed(request);
      await this.abortRequestTransport(request);
      throw error instanceof ChatServiceError
        ? error
        : new ChatServiceError(error instanceof Error ? error.message : 'Stream reading failed');
    } finally {
      if (request.reader === reader) {
        request.reader = null;
      }
      try {
        reader.releaseLock();
      } catch {
        // The reader may already be released after cancellation.
      }
    }
  }

  private async appendAssistantMessage(content: string, toolCalls?: ChatMessage['tool_calls']): Promise<ChatMessage | null> {
    if (!content && !toolCalls?.length) {
      return null;
    }

    const assistantMessage: ChatMessage = {
      id: Date.now(),
      messageId: this.nextMessageId,
      role: 'assistant',
      content,
      timestamp: new Date().toISOString(),
      ...(toolCalls?.length ? { tool_calls: toolCalls } : {})
    };

    const requiresDurablePersistence = toolCalls?.some((toolCall) =>
      toolCall.function.name === 'ask_user_input'
    ) ?? false;
    if (requiresDurablePersistence) {
      await this.persistMessage(assistantMessage, true);
    }

    this.nextMessageId++;
    this.messages.push(assistantMessage);
    this.saveMessages(this.messages);
    if (!requiresDurablePersistence) {
      await this.persistMessage(assistantMessage);
    }

    return assistantMessage;
  }

  private getPendingHumanInputFromToolCalls(toolCalls?: ChatMessage['tool_calls']): {
    pendingHumanInput: PendingHumanInput;
    request: HumanInputRequest;
  } | null {
    for (const toolCall of toolCalls ?? []) {
      if (
        toolCall.function.name !== 'ask_user_input' ||
        this.hasToolResponseFor(toolCall.id) ||
        this.getCancelledHumanInputToolCallIds(this.messages).has(toolCall.id)
      ) {
        continue;
      }

      const request = this.safeParseHumanInputRequest(toolCall.function.arguments);
      if (!request) {
        continue;
      }

      return {
        pendingHumanInput: {
          toolCallId: toolCall.id,
          toolName: toolCall.function.name,
          request
        },
        request
      };
    }

    return null;
  }

  private upsertToolProgressMessage(params: {
    toolId?: string;
    toolName: string;
    status: 'pending' | 'success' | 'error';
    message: string;
    eventType: string;
    details?: any;
    executionSource?: 'server' | 'client';
  }): ChatMessage {
    const existingProgressMessage = this.messages.find(m =>
      m.isUIOnly &&
      m.sseEventType === 'tool_call' &&
      params.toolId &&
      m.eventData?.details?.toolId === params.toolId
    );

    if (existingProgressMessage) {
      existingProgressMessage.content = params.message;
      existingProgressMessage.timestamp = new Date().toISOString();
      existingProgressMessage.eventData = {
        ...existingProgressMessage.eventData!,
        eventType: params.eventType,
        status: params.status,
        message: params.message,
        details: {
          ...existingProgressMessage.eventData?.details,
          ...params.details,
          toolName: params.toolName,
          toolId: params.toolId
        }
      };
      this.saveMessages(this.messages);
      return existingProgressMessage;
    }

    const toolCallProgressMessage: ChatMessage = {
      id: Date.now() + Math.random(),
      role: 'sse_event',
      content: params.message,
      timestamp: new Date().toISOString(),
      isUIOnly: true,
      sseEventType: 'tool_call',
      execution_source: params.executionSource ?? 'server',
      eventData: {
        eventType: params.eventType,
        status: params.status,
        message: params.message,
        details: {
          ...params.details,
          toolName: params.toolName,
          toolId: params.toolId
        }
      }
    };

    this.messages.push(toolCallProgressMessage);
    this.saveMessages(this.messages);
    return toolCallProgressMessage;
  }

  private attemptExternalDraftOpen(url: string): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    let openedWindow: Window | null;
    try {
      openedWindow = window.open(url, '_blank');
    } catch {
      return false;
    }
    if (!openedWindow) {
      return false;
    }

    try {
      openedWindow.opener = null;
    } catch {
      // Cross-origin browser restrictions do not change whether the draft opened.
    }
    return true;
  }

  private appendClientActionMessage(event: ClientActionEvent): ChatMessage {
    let actionUrl: string | undefined;
    let googleTaskClipboardText: string | undefined;
    let automaticOpenStatus: 'not-applicable' | 'opened' | 'blocked' = 'not-applicable';
    let content: string;

    switch (event.action) {
      case 'open_note_draft':
        this.eventBus?.publish(EVENTS.OPEN_NOTE_MODAL, {
          accountId: event.payload.accountId,
          contactId: event.payload.contactId,
          teamId: event.payload.teamId,
          initialContent: event.payload.content,
        });
        content = 'Note draft opened';
        break;

      case 'open_task_draft':
        this.eventBus?.publish(EVENTS.OPEN_TASK_MODAL, {
          accountId: event.payload.accountId,
          accountName: event.payload.accountName,
          contactId: event.payload.contactId,
          contactName: event.payload.contactName,
          initialTitle: event.payload.title,
          initialDescription: event.payload.description,
          initialDueDate: event.payload.dueDate,
          initialPriority: event.payload.priority,
        });
        content = 'Task draft opened';
        break;

      case 'open_email_draft':
        actionUrl = buildGmailUrl(
          event.payload.subject ?? '',
          event.payload.body ?? '',
          event.payload.to?.join(','),
          event.payload.cc?.join(','),
          event.payload.bcc?.join(','),
        );
        automaticOpenStatus = this.attemptExternalDraftOpen(actionUrl) ? 'opened' : 'blocked';
        content = automaticOpenStatus === 'opened' ? 'Email draft opened in Gmail' : 'Email draft ready to open';
        break;

      case 'open_meeting_draft': {
        const start = event.payload.start ? new Date(event.payload.start) : undefined;
        const end = event.payload.end ? new Date(event.payload.end) : undefined;
        actionUrl = buildGoogleCalendarUrl(
          event.payload.title,
          event.payload.description ?? '',
          start,
          end,
          event.payload.location,
        );
        automaticOpenStatus = this.attemptExternalDraftOpen(actionUrl) ? 'opened' : 'blocked';
        content = automaticOpenStatus === 'opened' ? 'Meeting draft opened in Google Calendar' : 'Meeting draft ready to open';
        break;
      }

      case 'open_google_task':
        actionUrl = buildGoogleTasksUrl();
        googleTaskClipboardText = formatGoogleTaskClipboardText(event.payload);
        automaticOpenStatus = this.attemptExternalDraftOpen(actionUrl) ? 'opened' : 'blocked';
        content = automaticOpenStatus === 'opened' ? 'Google Task details ready' : 'Google Task details ready to open';
        break;
    }

    const message: ChatMessage = {
      id: Date.now() + Math.random(),
      role: 'sse_event',
      content,
      timestamp: new Date().toISOString(),
      isUIOnly: true,
      sseEventType: 'client_action',
      execution_source: 'client',
      eventData: {
        eventType: 'client.action',
        status: automaticOpenStatus === 'blocked' ? 'blocked' : 'completed',
        message: content,
        details: {
          actionId: event.actionId,
          action: event.action,
          automaticOpenStatus,
          ...(actionUrl ? { actionUrl } : {}),
          ...(googleTaskClipboardText ? { googleTaskClipboardText } : {}),
        },
      },
    };

    this.messages.push(message);
    this.saveMessages(this.messages);
    return message;
  }

  private appendHumanInputPrompt(data: {
    pendingHumanInput?: PendingHumanInput;
    request?: HumanInputRequest;
  }): ChatMessage | null {
    const pendingHumanInput = data.pendingHumanInput;
    const request = data.request ?? pendingHumanInput?.request;

    if (!pendingHumanInput || !request || !Array.isArray(request.questions)) {
      return null;
    }

    const existingPrompt = this.messages.find((message) =>
      message.isUIOnly &&
      message.sseEventType === 'human_input' &&
      message.eventData?.details?.pendingHumanInput?.toolCallId === pendingHumanInput.toolCallId
    );
    if (existingPrompt) {
      return existingPrompt;
    }

    const firstQuestion = request.questions[0];
    const promptMessage: ChatMessage = {
      id: Date.now() + Math.random(),
      role: 'sse_event',
      content: firstQuestion?.question || 'Input needed to continue',
      timestamp: new Date().toISOString(),
      isUIOnly: true,
      sseEventType: 'human_input',
      execution_source: 'client',
      eventData: {
        eventType: 'ask_user_input',
        status: 'pending',
        message: firstQuestion?.question || 'Input needed to continue',
        details: {
          pendingHumanInput,
          request
        }
      }
    };

    this.messages.push(promptMessage);
    this.saveMessages(this.messages);
    return promptMessage;
  }

  private safeParseHumanInputRequest(rawArguments: string): HumanInputRequest | null {
    return parseHumanInputRequest(rawArguments);
  }

  private hasToolResponseFor(toolCallId: string): boolean {
    return this.messages.some((message) =>
      !message.isUIOnly &&
      message.role === 'tool' &&
      message.tool_call_id === toolCallId
    );
  }

  private hasPendingHumanInputToolCall(): boolean {
    const cancelledToolCallIds = this.getCancelledHumanInputToolCallIds(this.messages);
    return this.messages.some((message) =>
      message.role === 'assistant' &&
      message.tool_calls?.some((toolCall) =>
        toolCall.function.name === 'ask_user_input' &&
        !this.hasToolResponseFor(toolCall.id) &&
        !cancelledToolCallIds.has(toolCall.id)
      )
    );
  }

  private assertNoPendingHumanInput(action: string): void {
    this.assertHistoryReady();
    if (this.hasPendingHumanInputToolCall()) {
      throw new ChatServiceError(`Answer the pending input request before you ${action}`);
    }
  }

  private assertHistoryReady(): void {
    if (this.historyReconciliationRequired) {
      throw new ChatServiceError(
        'Chat history could not be reconciled. Reload the session before continuing.'
      );
    }
  }

  private normalizeHumanInputAnswer(
    request: HumanInputRequest,
    answer: HumanInputAnswer
  ): HumanInputAnswer {
    const result = normalizeHumanInputOutcome(request, answer);
    if (!result.ok) {
      throw new ChatServiceError(result.error);
    }
    if (
      result.outcome.status === 'cancelled' &&
      (request.allowSkip !== true || result.outcome.reason !== 'skipped')
    ) {
      throw new ChatServiceError('This human input prompt cannot be skipped');
    }

    return result.outcome;
  }

  private usesFreeformHumanInputAnswer(
    request: HumanInputRequest,
    answer: HumanInputAnswer
  ): boolean {
    if (answer.status !== 'answered') {
      return false;
    }

    return request.questions.some((question) => {
      const answerValue = answer.answers[question.id];
      const selectedValues = Array.isArray(answerValue) ? answerValue : [answerValue];
      const optionIds = new Set(question.options.map((option) => option.id));
      return selectedValues.some((value) => !optionIds.has(value));
    });
  }

  private assertHumanInputHistoryIsComplete(
    messages: ChatMessage[],
    pendingHumanInput: PendingHumanInput,
    toolMessage: ChatMessage
  ): void {
    const candidateHistory = this.buildRecentHistory(messages);
    const hasCompleteAssistantToolCall = candidateHistory.some((message) =>
      message.role === 'assistant' &&
      message.tool_calls?.some((toolCall: NonNullable<ChatMessage['tool_calls']>[number]) =>
        toolCall.id === pendingHumanInput.toolCallId &&
        toolCall.function.name === pendingHumanInput.toolName
      )
    );
    const hasCompleteToolResponse = candidateHistory.some((message) =>
      message.role === 'tool' &&
      message.tool_call_id === pendingHumanInput.toolCallId &&
      message.name === pendingHumanInput.toolName &&
      message.content === toolMessage.content
    );

    if (!hasCompleteAssistantToolCall || !hasCompleteToolResponse) {
      throw new ChatServiceError('Human input response is too large to resume safely');
    }
  }

  private completeHumanInputPrompts(toolCallId: string, answer: HumanInputAnsweredOutcome): void {
    for (const message of this.messages) {
      if (
        !message.isUIOnly ||
        message.sseEventType !== 'human_input' ||
        message.eventData?.details?.pendingHumanInput?.toolCallId !== toolCallId
      ) {
        continue;
      }

      message.content = 'Answered requested input';
      message.timestamp = new Date().toISOString();
      message.eventData = {
        ...message.eventData!,
        status: 'success',
        message: message.content,
        details: {
          ...message.eventData?.details,
          outcome: answer
        }
      };
    }
  }

  private restorePendingHumanInputPrompts(): void {
    const cancelledToolCallIds = this.getCancelledHumanInputToolCallIds(this.messages);
    for (const message of [...this.messages]) {
      if (message.role !== 'assistant' || !message.tool_calls?.length) {
        continue;
      }

      for (let toolCallIndex = 0; toolCallIndex < message.tool_calls.length; toolCallIndex += 1) {
        const toolCall = message.tool_calls[toolCallIndex];
        if (
          toolCall.function.name !== 'ask_user_input' ||
          this.hasToolResponseFor(toolCall.id) ||
          cancelledToolCallIds.has(toolCall.id)
        ) {
          continue;
        }

        const canonicalToolCall =
          canonicalizeComposerHumanInputToolCalls([toolCall])?.[0] ?? toolCall;
        if (canonicalToolCall !== toolCall) {
          this.pendingCanonicalToolCallUpdates.add(toolCall.id);
        }
        message.tool_calls[toolCallIndex] = canonicalToolCall;

        const request = this.safeParseHumanInputRequest(canonicalToolCall.function.arguments);
        if (!request) {
          continue;
        }

        this.appendHumanInputPrompt({
          pendingHumanInput: {
            toolCallId: canonicalToolCall.id,
            toolName: canonicalToolCall.function.name,
            request
          },
          request
        });
      }
    }
  }

  private formatHumanInputAnswer(request: HumanInputRequest, answer: HumanInputAnsweredOutcome): string {
    const lines = request.questions.map((question) => {
      const value = answer.answers[question.id];
      const selectedIds = Array.isArray(value) ? value : value ? [value] : [];
      const labels = selectedIds.map((id) =>
        question.options.find((option) => option.id === id)?.label ?? id
      );

      return `${question.header}: ${labels.join(', ')}`;
    }).filter((line) => !line.endsWith(': '));

    return lines.length > 0 ? lines.join('\n') : 'Answered requested input';
  }

  private restoreCompletedHumanInputAnswerMessages(): void {
    const toolCallsById = new Map<string, Array<{
      message: ChatMessage;
      toolCall: NonNullable<ChatMessage['tool_calls']>[number];
    }>>();
    const toolRowsById = new Map<string, ChatMessage[]>();
    const cancelledToolCallIds = this.getCancelledHumanInputToolCallIds(this.messages);
    const durableProtocolMessages = this.messages.filter((message) =>
      !message.isUIOnly &&
      (message.role === 'user' || message.role === 'assistant' || message.role === 'tool')
    );
    const durableProtocolIndex = new Map(
      durableProtocolMessages.map((message, index) => [message, index])
    );

    for (const message of this.messages) {
      if (message.role === 'assistant') {
        for (const toolCall of message.tool_calls ?? []) {
          const matches = toolCallsById.get(toolCall.id) ?? [];
          matches.push({ message, toolCall });
          toolCallsById.set(toolCall.id, matches);
        }
      }

      if (message.role === 'tool' && message.tool_call_id) {
        const matches = toolRowsById.get(message.tool_call_id) ?? [];
        matches.push(message);
        toolRowsById.set(message.tool_call_id, matches);
      }
    }

    this.messages = this.messages.flatMap((message) => {
      if (
        message.role !== 'tool' ||
        !message.tool_call_id ||
        message.name !== 'ask_user_input'
      ) {
        return [message];
      }

      const matchingToolCalls = toolCallsById.get(message.tool_call_id) ?? [];
      const matchingToolRows = toolRowsById.get(message.tool_call_id) ?? [];
      const matchingToolCall = matchingToolCalls[0];
      const durableId = Number(message.id);
      const assistantIndex = matchingToolCall
        ? durableProtocolIndex.get(matchingToolCall.message)
        : undefined;
      const toolIndex = durableProtocolIndex.get(message);

      if (
        matchingToolCalls.length !== 1 ||
        matchingToolRows.length !== 1 ||
        !matchingToolCall ||
        matchingToolCall.toolCall.function.name !== 'ask_user_input' ||
        assistantIndex === undefined ||
        toolIndex !== assistantIndex + 1 ||
        cancelledToolCallIds.has(message.tool_call_id) ||
        !Number.isInteger(durableId) ||
        durableId <= 0
      ) {
        return [message];
      }

      const request = parseHumanInputRequest(
        matchingToolCall.toolCall.function.arguments
      );
      const outcome = request
        ? parseCanonicalHumanInputAnswer(request, message.content)
        : null;

      if (!request || !outcome || outcome.status !== 'answered') {
        return [message];
      }

      const displayMessage: ChatMessage = {
        id: -durableId,
        role: 'user',
        content: this.formatHumanInputAnswer(request, outcome),
        timestamp: message.timestamp,
        isUIOnly: true
      };

      return [displayMessage, message];
    });
  }

  private async completeAssistantRequest(
    request: ActiveChatRequest,
    content: string,
    toolCalls?: ChatMessage['tool_calls']
  ): Promise<ChatMessage | null> {
    if (request.state !== 'running') {
      return request.terminalMessage;
    }

    request.state = 'completing';
    try {
      const canonicalToolCalls = canonicalizeComposerHumanInputToolCalls(toolCalls);
      const assistantMessage = await this.appendAssistantMessage(content, canonicalToolCalls);
      const pendingHumanInput = this.getPendingHumanInputFromToolCalls(canonicalToolCalls);
      const promptMessage = pendingHumanInput
        ? this.appendHumanInputPrompt(pendingHumanInput)
        : null;
      const terminalMessage = promptMessage ?? assistantMessage;

      if (!terminalMessage) {
        request.state = 'failed';
        throw new ChatServiceError('Stream completed without a response');
      }

      request.assistantContent = '';
      request.terminalMessage = terminalMessage;
      request.state = 'completed';
      return terminalMessage;
    } catch (error) {
      request.state = 'failed';
      throw error;
    }
  }

  submitHumanInput(
    promptMessageId: number,
    answer: HumanInputAnswer,
    onChunk: (content: string) => void
  ): ChatRequestHandle {
    try {
      this.assertHistoryReady();
    } catch (error) {
      return this.createRejectedRequestHandle(error);
    }

    const promptMessage = this.messages.find((message) => message.id === promptMessageId);
    const details = promptMessage?.eventData?.details;
    const pendingHumanInput = details?.pendingHumanInput as PendingHumanInput | undefined;
    const request = details?.request as HumanInputRequest | undefined;

    if (
      !promptMessage ||
      !pendingHumanInput ||
      !request ||
      promptMessage.eventData?.status !== 'pending' ||
      this.hasToolResponseFor(pendingHumanInput.toolCallId)
    ) {
      return this.createRejectedRequestHandle(
        new ChatServiceError('Human input prompt is no longer available')
      );
    }

    if (this.pendingHumanInputSubmissions.has(pendingHumanInput.toolCallId)) {
      return this.createRejectedRequestHandle(
        new ChatServiceError('Human input response is already being submitted')
      );
    }

    return this.createRequestHandle(async (activeRequest) => {
      this.pendingHumanInputSubmissions.add(pendingHumanInput.toolCallId);
      try {
        const normalizedAnswer = this.normalizeHumanInputAnswer(request, answer);
        if (
          this.pendingCanonicalToolCallUpdates.has(pendingHumanInput.toolCallId) &&
          this.usesFreeformHumanInputAnswer(request, normalizedAnswer)
        ) {
          await this.persistCanonicalHumanInputRequest(pendingHumanInput);
        }
        if (normalizedAnswer.status === 'cancelled') {
          const cancellationMessage: ChatMessage = {
            ...promptMessage,
            messageId: this.nextMessageId,
            content: 'Skipped requested input',
            timestamp: new Date().toISOString(),
            isUIOnly: true,
            role: 'sse_event',
            sseEventType: 'human_input',
            eventData: {
              ...promptMessage.eventData!,
              eventType: 'ask_user_input',
              status: 'cancelled',
              message: 'Skipped requested input',
              details: {
                ...promptMessage.eventData?.details,
                pendingHumanInput,
                request,
                outcome: normalizedAnswer
              }
            }
          };

          await this.persistHumanInputCancellation(cancellationMessage);
          this.pendingCanonicalToolCallUpdates.delete(pendingHumanInput.toolCallId);
          for (let index = 0; index < this.messages.length; index++) {
            const message = this.messages[index];
            if (
              message.sseEventType !== 'human_input' ||
              message.eventData?.details?.pendingHumanInput?.toolCallId !== pendingHumanInput.toolCallId
            ) {
              continue;
            }
            this.messages[index] = message.id === promptMessage.id
              ? cancellationMessage
              : {
                ...message,
                content: cancellationMessage.content,
                timestamp: cancellationMessage.timestamp,
                eventData: cancellationMessage.eventData
              };
          }
          this.nextMessageId++;
          this.saveMessages(this.messages);
          activeRequest.terminalMessage = cancellationMessage;
          activeRequest.state = 'completed';
          return cancellationMessage;
        }

        const toolMessage: ChatMessage = {
          id: Date.now() + Math.random(),
          messageId: this.nextMessageId,
          role: 'tool',
          name: pendingHumanInput.toolName,
          tool_call_id: pendingHumanInput.toolCallId,
          content: JSON.stringify(normalizedAnswer),
          timestamp: new Date().toISOString()
        };
        this.assertHumanInputHistoryIsComplete(
          [...this.messages, toolMessage],
          pendingHumanInput,
          toolMessage
        );

        const displayMessage: ChatMessage = {
          id: Date.now() + Math.random(),
          role: 'user',
          content: this.formatHumanInputAnswer(request, normalizedAnswer),
          timestamp: new Date().toISOString(),
          isUIOnly: true
        };

        try {
          await this.persistMessage(toolMessage, true);
        } catch (error) {
          this.markRequestFailed(activeRequest);
          throw error;
        }
        this.pendingCanonicalToolCallUpdates.delete(pendingHumanInput.toolCallId);
        this.completeHumanInputPrompts(pendingHumanInput.toolCallId, normalizedAnswer);
        this.nextMessageId++;
        this.messages.push(displayMessage, toolMessage);
        this.saveMessages(this.messages);

        try {
          return await this.fetchChatResponse(activeRequest, onChunk);
        } catch (error) {
          if (this.isRequestedAbort(activeRequest, error)) {
            return this.finalizeStoppedRequest(activeRequest);
          }
          this.markRequestFailed(activeRequest);
          console.error('❌ Error submitting human input:', {
            error: error instanceof Error ? error.message : 'Unknown error',
            errorStack: error instanceof Error ? error.stack : undefined,
            promptMessageId,
            answer
          });
          throw new ChatServiceError(error instanceof Error ? error.message : 'Failed to submit human input');
        }
      } finally {
        this.pendingHumanInputSubmissions.delete(pendingHumanInput.toolCallId);
      }
    });
  }

  private async processEvent(
    evt: string,
    onChunk: (content: string) => void,
    request: ActiveChatRequest
  ): Promise<ChatMessage | null> {
    const lines = evt.split('\n');
    const eventType = lines.find(l => l.startsWith('event:'))?.slice(6).trim();
    const dataLine = lines
      .filter(l => l.startsWith('data:'))
      .map(l => l.slice(5).trim())
      .join('\n');

    if (!dataLine) {
      return null;
    }

    try {
      const data = JSON.parse(dataLine);
      const normalizedEventType = eventType || data.type;

      if (request.state === 'stop-requested') {
        return this.finalizeStoppedRequest(request);
      }

      if (request.state === 'completed' && normalizedEventType !== 'done') {
        return null;
      }

      switch (normalizedEventType) {
        case 'ready':
          break;

        case 'message':
          if (request.state === 'running' && data.content) {
            onChunk(data.content);
            request.assistantContent += data.content;
          }
          break;

        case 'message.delta':
          if (request.state === 'running' && data.text) {
            onChunk(data.text);
            request.assistantContent += data.text;
          }
          break;

        case 'message.done': {
          const content = data.message?.content ?? request.assistantContent;
          const toolCalls = data.message?.tool_calls as ChatMessage['tool_calls'] | undefined;
          await this.completeAssistantRequest(request, content, toolCalls);
          break;
        }

        case 'client.action': {
          if (request.state !== 'running') {
            break;
          }
          const clientAction = parseClientActionEvent(data);
          if (!clientAction) {
            throw new ChatServiceError('Invalid frontend action event received');
          }
          const actionMessage = this.appendClientActionMessage(clientAction);
          request.progressMessageIds.add(actionMessage.id);
          onChunk('');
          break;
        }

        case 'tool.call':
          if (request.state === 'running' && data.name) {
            const progressMessage = this.upsertToolProgressMessage({
              toolId: data.toolCallId,
              toolName: data.name,
              status: 'pending',
              message: `Executing ${data.name}...`,
              eventType: 'tool.call',
              details: { args: data.args },
              executionSource: 'server'
            });
            request.progressMessageIds.add(progressMessage.id);
            onChunk('');
          }
          break;

        case 'tool.result':
          if (request.state === 'running' && data.name) {
            const hasError = Boolean(data.result?.error);
            const progressMessage = this.upsertToolProgressMessage({
              toolId: data.toolCallId,
              toolName: data.name,
              status: hasError ? 'error' : 'success',
              message: hasError ? `${data.name} failed` : `${data.name} completed successfully`,
              eventType: 'tool.result',
              details: {
                args: data.args,
                result: data.result,
                durationMs: data.durationMs
              },
              executionSource: 'server'
            });
            request.progressMessageIds.add(progressMessage.id);
            onChunk('');
          }
          break;

        case 'warning': {
          const warningMessage: ChatMessage = {
            id: Date.now() + Math.random(),
            role: 'sse_event',
            content: data.warning || 'Chat runtime warning',
            timestamp: new Date().toISOString(),
            isUIOnly: true,
            sseEventType: 'progress',
            execution_source: 'server',
            eventData: {
              eventType: 'warning',
              status: 'warning',
              message: data.warning,
              details: { code: data.code }
            }
          };
          this.messages.push(warningMessage);
          this.saveMessages(this.messages);
          onChunk('');
          break;
        }

        case 'done':
          if (request.state === 'running' && request.assistantContent) {
            await this.completeAssistantRequest(request, request.assistantContent);
          }
          if (request.state === 'completed' && request.terminalMessage) {
            return request.terminalMessage;
          }
          break;

        case 'error':
          throw new ChatServiceError(data.error || data.message || 'Unknown error occurred');

        default:
          break;
      }
    } catch (error) {
      if (error instanceof ChatServiceError) {
        throw error;
      }

      console.error('❌ Failed to parse event data:', {
        error: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined,
        eventType,
        dataLine,
        rawEvent: evt,
        parseAttempt: dataLine ? 'JSON.parse attempted' : 'No data line found'
      });
      throw new ChatServiceError(`Failed to parse event data: ${error instanceof Error ? error.message : 'Unknown error'}. Event type: ${eventType}, Data: ${dataLine}`);
    }

    return null;
  }

  getMessages(): ChatMessage[] {
    return this.messages;
  }

  isHistoryReconciliationRequired(): boolean {
    return this.historyReconciliationRequired;
  }

  saveMessages(messages: ChatMessage[]): void {
    this.messages = messages;
    if (messages.length === 0) {
      this.pendingCanonicalToolCallUpdates.clear();
    }
  }

  // Edit message and truncate history after the edited message
  async editMessage(messageId: number, newContent: string): Promise<void> {
    this.assertHistoryReady();

    const messageIndex = this.messages.findIndex(msg => msg.id === messageId);

    if (messageIndex === -1) {
      console.error('Message not found for editing:', messageId);
      return;
    }

    const message = this.messages[messageIndex];
    const dbMessageId = message.messageId; // Use sequential messageId for database operations

    if (
      message.role !== 'user' ||
      message.isUIOnly ||
      dbMessageId === undefined ||
      dbMessageId === null
    ) {
      throw new ChatServiceError('Only durable user messages can be edited');
    }

    const removedToolCallIds = this.messages
      .slice(messageIndex + 1)
      .flatMap((removedMessage) => removedMessage.tool_calls ?? [])
      .filter((toolCall) => toolCall.function.name === 'ask_user_input')
      .map((toolCall) => toolCall.id);
    const originalLaterDurableSignature = this.messages
      .slice(messageIndex + 1)
      .filter((candidate) => !candidate.isUIOnly)
      .map((candidate) => JSON.stringify({
        messageId: candidate.messageId,
        role: candidate.role,
        content: candidate.content,
        name: candidate.name,
        toolCallId: candidate.tool_call_id,
        toolCalls: candidate.tool_calls
      }));

    // Replace the durable branch in one transaction before changing local state.
    if (this.currentSessionId) {
      try {
        await chatSessionService.replaceEditedMessageBranch(
          this.currentSessionId,
          dbMessageId,
          newContent
        );
      } catch (error) {
        console.error('❌ Failed to replace edited message branch:', error);
        // The transaction may have committed even if its HTTP acknowledgement
        // was lost. Reconcile from the uncached durable route before deciding.
        await this.reconcileSessionMessages(this.currentSessionId);
        const reconciledMessage = this.messages.find((candidate) =>
          candidate.messageId === dbMessageId
        );
        const hasLaterDurableMessage = this.messages.some((candidate) =>
          !candidate.isUIOnly &&
          candidate.messageId !== undefined &&
          candidate.messageId > dbMessageId
        );
        const reconciledLaterDurableSignature = this.messages
          .filter((candidate) =>
            !candidate.isUIOnly &&
            candidate.messageId !== undefined &&
            candidate.messageId > dbMessageId
          )
          .map((candidate) => JSON.stringify({
            messageId: candidate.messageId,
            role: candidate.role,
            content: candidate.content,
            name: candidate.name,
            toolCallId: candidate.tool_call_id,
            toolCalls: candidate.tool_calls
          }));

        if (reconciledMessage?.content === newContent && !hasLaterDurableMessage) {
          this.eventBus?.publish(EVENTS.CHAT_SESSION_UPDATED, {
            sessionId: this.currentSessionId
          });
          return;
        }

        const confirmedRollback =
          reconciledMessage?.content === message.content &&
          JSON.stringify(reconciledLaterDurableSignature) ===
            JSON.stringify(originalLaterDurableSignature);
        if (!confirmedRollback) {
          this.historyReconciliationRequired = true;
          throw new ChatServiceError(
            'Edited history is inconsistent after reconciliation. Reload the session before continuing.'
          );
        }

        throw error;
      }
    }

    // Commit local branch replacement only after durable update and deletion succeed.
    this.messages[messageIndex] = {
      ...this.messages[messageIndex],
      content: newContent
    };
    this.messages = this.messages.slice(0, messageIndex + 1);
    removedToolCallIds.forEach((toolCallId) => {
      this.pendingCanonicalToolCallUpdates.delete(toolCallId);
    });

    // Reset nextMessageId to be after the edited message.
    this.nextMessageId = dbMessageId + 1;
    this.eventBus?.publish(EVENTS.CHAT_SESSION_UPDATED, {
      sessionId: this.currentSessionId
    });
  }

  // Resubmit conversation after edit (without adding new user message)
  resubmitAfterEdit(
    onChunk: (content: string) => void
  ): ChatRequestHandle {
    try {
      this.assertNoPendingHumanInput('resubmit edited history');
    } catch (error) {
      return this.createRejectedRequestHandle(error);
    }

    return this.createRequestHandle(async (request) => {
      try {
        return await this.fetchChatResponse(request, onChunk);
      } catch (error) {
        if (this.isRequestedAbort(request, error)) {
          return this.finalizeStoppedRequest(request);
        }
        this.markRequestFailed(request);
        console.error('❌ Error resubmitting after edit:', {
          error: error instanceof Error ? error.message : 'Unknown error',
          errorStack: error instanceof Error ? error.stack : undefined,
          errorType: error?.constructor?.name,
          messageHistory: this.getRecentHistory(),
          requestDetails: {
            url: '/api/chat',
            method: 'POST',
            headers: 'application/json'
          }
        });
        throw new ChatServiceError(error instanceof Error ? error.message : 'Failed to resubmit conversation');
      }
    });
  }

  // Set event bus for external communication
  setEventBus(eventBus: EventBus) {
    this.eventBus = eventBus;
  }
}

export const chatService = new ChatService();
